import { pdfjs, Document, Page } from 'react-pdf';
import React, { useState, useMemo } from 'react';
import {
  Box,
  Spinner,
  Text,
  IconButton,
  CloseIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  Button,
} from '@razorpay/blade/components';

import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

import { overlayLayers } from 'constants/overlayLayers.generated';
import type { FileType, PDFViewerProps, FileState } from './types';

pdfjs.GlobalWorkerOptions.workerSrc = require('pdfjs-dist/build/pdf.worker.min.js');

const getFileType = (url: string): FileType => {
  const lowercaseUrl = url.toLowerCase();
  if (lowercaseUrl.endsWith('.pdf')) return 'pdf';
  if (
    lowercaseUrl.endsWith('.png') ||
    lowercaseUrl.endsWith('.jpg') ||
    lowercaseUrl.endsWith('.jpeg')
  ) {
    return 'image';
  }

  return 'image';
};

export const PDFViewer = ({ files, onClose, isOpen = false }: PDFViewerProps) => {
  const [state, setState] = useState<FileState>({
    currentFileIndex: 0,
    currentPageIndex: 0,
    isLoading: true,
    error: null,
    totalPages: 0,
  });

  const currentFile = useMemo(() => {
    const file = files[state.currentFileIndex];

    if (file && 'url' in file && file?.url) {
      return file;
    } else if (file && (file instanceof File || 'webkitRelativePath' in file)) {
      const url = URL.createObjectURL(file as File);
      return { name: file.name, url, type: getFileType(file.name) };
    }
    return file;
  }, [files, state.currentFileIndex]);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setState((prev) => ({
      ...prev,
      isLoading: false,
      totalPages: numPages,
      error: null,
    }));
  };

  const handleImageLoad = () => {
    setState((prev) => ({
      ...prev,
      isLoading: false,
      error: null,
    }));
  };

  const handleImageError = () => {
    setState((prev) => ({
      ...prev,
      isLoading: false,
      error: 'Failed to load image',
    }));
  };

  const onPrevClick = () => {
    setState((prev) => ({ ...prev, currentFileIndex: prev.currentFileIndex - 1 }));
  };

  const onNextClick = () => {
    setState((prev) => ({ ...prev, currentFileIndex: prev.currentFileIndex + 1 }));
  };

  const isMultipleFiles = files.length > 1;
  const fileType = currentFile?.type || getFileType(currentFile?.url || '');

  return isOpen ? (
    <Box
      display="flex"
      flexDirection="column"
      height="100vh"
      width="100vw"
      backgroundColor="overlay.background.subtle"
      testID="pdf-viewer"
      position={'fixed'}
      top={'0px'}
      left={'0px'}
      zIndex={overlayLayers.modal}>
      {onClose && (
        <IconButton
          icon={CloseIcon}
          accessibilityLabel="Close viewer"
          onClick={onClose}
          emphasis="subtle"
          position={'fixed'}
          top="spacing.7"
          right={'spacing.7'}
        />
      )}

      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        overflow="auto"
        alignItems="center"
        justifyContent="center"
        padding="spacing.4">
        <Box marginY={'spacing.9'} height={'calc(100% - 88px)'}>
          {state.error ? (
            <Text color="surface.text.gray.muted" size="large" textAlign="center">
              {state.error}
            </Text>
          ) : (
            <Box display={'flex'} alignItems={'center'} gap="156px" flexDirection={'row'}>
              {isMultipleFiles && (
                <Button
                  icon={ArrowLeftIcon}
                  onClick={onPrevClick}
                  color="white"
                  accessibilityLabel="previous"
                  position={'fixed'}
                  top="50%"
                  left={'25%'}
                  isDisabled={state.currentFileIndex === 0}
                  zIndex={overlayLayers.nested}
                />
              )}
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="spacing.3"
                  marginBottom={'spacing.5'}
                  justifyContent={'space-between'}>
                  <Text size="small" color="surface.text.gray.normal">
                    {currentFile?.name || 'Document'}
                  </Text>
                  {isMultipleFiles && (
                    <Text size="small" color="surface.text.gray.normal">
                      {state.currentFileIndex + 1} of {files.length}
                    </Text>
                  )}
                </Box>
                {fileType === 'pdf' ? (
                  <Document
                    file={currentFile.url}
                    onLoad={handleDocumentLoadSuccess}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    loading={
                      <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        height={'calc(100% - 76px)'}>
                        <Spinner size="large" color="primary" accessibilityLabel="Loading PDF" />
                      </Box>
                    }>
                    {Array.from(new Array(state.totalPages || 0), (_, index) => (
                      <Box key={`page-${index + 1}`} marginTop={'spacing.5'}>
                        <Page pageNumber={index + 1} />
                      </Box>
                    ))}
                  </Document>
                ) : (
                  <img
                    src={currentFile.url}
                    onLoad={handleImageLoad}
                    onError={() => handleImageError()}
                    style={{
                      borderRadius: '8px',
                      objectFit: 'contain',
                      height: 'calc(100vh - 88px)',
                      maxWidth: '650px',
                    }}
                    alt={currentFile?.name}
                  />
                )}
              </Box>
              {isMultipleFiles && (
                <Button
                  variant="tertiary"
                  icon={ArrowRightIcon}
                  onClick={onNextClick}
                  color="white"
                  accessibilityLabel="next"
                  position={'fixed'}
                  top="50%"
                  right={'25%'}
                  isDisabled={state.currentFileIndex === files.length - 1}
                  zIndex={overlayLayers.nested}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  ) : null;
};
